import * as Yup from "yup";

import { Route, useHistory, useLocation } from "react-router-dom";

import AlertBox from "components/global/AlertBox";
import Button from "components/global/Button";
import FormGroup from "components/global/FormGroup";
import FormWrapper from "components/global/FormWrapper";
import { Formik } from "formik";
import { Link } from "react-router-dom";
import Logo from "components/global/Logo";
import Spacer from "components/global/Spacer";
import { axiosInstance } from "api";
import { showAlert } from "utils/utils";
import styled from "styled-components";
import { useState } from "react";
import verifyEmailImg from "assets/images/verify-email.svg";

const Wrapper = styled.div`
  padding: 4.8rem 7.2rem;
  position: relative;
  overflow: hidden;

  .header {
    a {
      color: var(--secondary);
    }
  }

  .content {
    width: 56rem;
    margin: auto;

    .description {
      display: block;
      width: 40rem;
      color: var(--body_text);
    }
  }

  @media screen and (max-width: 768px) {
    padding: 4.8rem 2.4rem;

    .header {
      h3 {
        font-size: 24px;
        line-height: 32px;
      }
    }

    .content {
      width: 100%;
      text-align: left;

      .title {
        width: 100%;
        font-size: 20px;
        font-weight: 700;
        line-height: 32px;

        &.fullWidth {
          width: 100%;
        }
      }

      .description {
        width: 100%;
        font-size: 14px;
        font-style: normal;
        font-weight: 450;
        line-height: 20px;
      }
    }

    .title-1 {
      font-size: 14px;
      line-height: 20px;

      a {
        font-size: 14px;
      }
    }

    label {
      font-size: 14px;
      font-style: normal;
      font-weight: 450;
    }
  }
`;

const ForgotPassword = () => {
  const router = useHistory();
  const { search } = useLocation();
  const userEmail = new URLSearchParams(search).get("email");
  const [emailAddress, setEmailAddress] = useState("");

  const schema_email = Yup.object({
    email: Yup.string()
      .email("Invalid email address")
      .required("Field required"),
  });

  const schema_password = Yup.object({
    password: Yup.string()
      .matches(
        // eslint-disable-next-line
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])[\w~@#$%^&*+=`|{}:;!.?\"()\[\]a-zA-Z0-9]{8,}$/,
        "Must contain a minimum of 8 characters, one uppercase letter, one lowercase letter, and one number"
      )
      .required("Field required"),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Field required"),
  });

  const submitEmail = async (values) => {
    setEmailAddress(values.email);
    try {
      let res = await axiosInstance.post(`forgot-password`, values);

      if (res.data.status === true) {
        router.push("/password-reset/submit/mail-sent");
      } else if (res.data.email) {
        showAlert({
          msg: res.data.email[0],
          type: "danger",
        });
      } else {
        showAlert({
          msg: "An error occurred. Try again.",
          type: "danger",
        });
      }
    } catch (e) {
      showAlert({
        msg: "Invalid request. Try again.",
        type: "danger",
      });
    }
  };

  const submitPassword = async (values) => {
    const token = window.location.pathname.split("/")[2];
    const data = { ...values, token, email: userEmail };

    try {
      let res = await axiosInstance.post(`reset-password`, data);

      if (res.data.status === true) {
        router.push("/sign-in");
        showAlert({
          msg: "Password changed sucessfully, sign in to continue using PostPaddy",
          type: "success",
        });
      } else if (res.data.status === false) {
        showAlert({
          msg: "An error occurred. Please try again",
          type: "danger",
        });
      } else {
        showAlert({
          msg: "Something went wrong",
          type: "danger",
        });
      }
    } catch (e) {
      showAlert({
        msg: "Invalid request. Try again.",
        type: "danger",
      });
    }
  };

  return (
    <Wrapper>
      <AlertBox />
      {/* Send mail */}
      <Route exact path="/password-reset">
        <div className="flexRow alignCenter justifySpaceBetween header">
          <Logo />
          <p className="title-1">
            Don't have an account?{" "}
            <Link to="/sign-up" className="bold">
              Sign up
            </Link>
          </p>
        </div>
        <Spacer y={14.4} yMobile={4.8} />
        <div className="content flexColumn alignCenter textCenter">
          <h3 className="colorGrey1 title">Forgot your password?</h3>
          <Spacer y={0.6} />
          <span className="description">
            Please enter your email address below to reset your password.
          </span>
          <Spacer y={4.8} yMobile={2.4} />
          <Formik
            initialValues={{
              email: "",
            }}
            validationSchema={schema_email}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              await submitEmail(values);
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, isSubmitting, isValid, values }) => (
              <FormWrapper onSubmit={handleSubmit}>
                <FormGroup
                  fieldStyle="shortText"
                  type="email"
                  label="Email address"
                  name="email"
                  className="spanFull"
                />
                <Button
                  type="submit"
                  text={"Send me the link"}
                  className="spanFull"
                  disabled={!isValid || !values.email || isSubmitting}
                  loading={isSubmitting}
                  fullWidth
                />
              </FormWrapper>
            )}
          </Formik>
          <Spacer y={1.2} />
          <p className="colorGrey1 body-2">
            Remember your password?{" "}
            <Link to="/sign-in" className="colorSecondary">
              Sign in
            </Link>
          </p>
        </div>
      </Route>

      {/* Check your mail */}
      <Route exact path="/password-reset/submit/mail-sent">
        <div className="flexRow alignCenter justifySpaceBetween">
          <Logo />
        </div>
        <Spacer y={14.4} yMobile={9.6} />
        <div className="content flexColumn alignCenter textCenter">
          <img
            src={verifyEmailImg}
            alt="Verification"
            className="verifyEmailImg"
          />
          <Spacer y={2.4} />
          <h3 className="title textCenter">Please check your email address</h3>
          <Spacer y={2.4} yMobile={0.2} />
          <p className="l1-large description textCenter">
            We sent an email to <b>{emailAddress || "you"}</b> containing a link
            to reset your password.
          </p>
        </div>
      </Route>

      {/* Reset password */}
      <Route exact path="/password-reset/:token">
        <div className="flexRow alignCenter justifySpaceBetween">
          <Logo />
          <p className="title-1">
            Don't have an account?{" "}
            <Link to="/sign-up" className="bold">
              Sign up
            </Link>
          </p>
        </div>
        <Spacer y={14.4} yMobile={4.8} />
        <div className="content flexColumn alignCenter textCenter">
          <h3 className="colorGrey1 title fullWidth">Create a new password</h3>
          <Spacer y={0.6} />
          <p className="description">
            Password must be at least a Minimium of 8 characters, one uppercase,
            one lowercase character and one number.
          </p>
          <Spacer y={4.8} />
          <Formik
            initialValues={{ password: "", password_confirmation: "" }}
            validationSchema={schema_password}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true);
              await submitPassword(values);
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, isValid, isSubmitting, values }) => (
              <FormWrapper onSubmit={handleSubmit}>
                <FormGroup
                  fieldStyle="shortText"
                  type="password"
                  label="New password"
                  name="password"
                  className="password spanFull"
                />
                <FormGroup
                  fieldStyle="shortText"
                  type="password"
                  label="Confirm password"
                  name="password_confirmation"
                  className="password spanFull"
                />
                <Button
                  type="submit"
                  text={"Change password"}
                  className="spanFull"
                  disabled={
                    !isValid ||
                    !values.password ||
                    !values.password_confirmation ||
                    isSubmitting
                  }
                  loading={isSubmitting}
                  fullWidth
                />
              </FormWrapper>
            )}
          </Formik>
          <Spacer y={1.2} />
          <p className="colorGrey1 body-2">
            Remember your password?{" "}
            <Link to="/sign-in" className="colorSecondary">
              Sign in
            </Link>
          </p>
        </div>
      </Route>
    </Wrapper>
  );
};

export default ForgotPassword;
